import type { ReactNode } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle } from '@paige/ui';
import type { InvoiceChannelView, PartnerAccount } from './pb-types';
import { Banner, FactList } from './pb-parts';
import { channelHint, channelWord, companiesWord, day, stateTone, stateWord } from './pb-words';
import './partner-billing.css';

/** An invoice, drawn: a sheet with lines, and a check for the statement Ademero sends. */
function InvoiceGlyph({ statement }: { statement: boolean }): ReactNode {
  return (
    <svg
      className="pb-channel__glyph"
      viewBox="0 0 48 48"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5h18l8 8v30H12z" />
      <path d="M30 5v8h8" />
      <path d="M17 21h14M17 27h14M17 33h8" />
      {statement ? <path d="M28 36l3 3 6-7" /> : <circle cx="33" cy="35" r="4" />}
    </svg>
  );
}

function ChannelHero({ c }: { c: InvoiceChannelView }): ReactNode {
  return (
    <div className="pb-channel">
      <InvoiceGlyph statement={c.channel === 'quickbooks'} />
      <div>
        <p className="pb-channel__name">{channelWord[c.channel]}</p>
        <p className="pb-channel__hint">{channelHint[c.channel]}</p>
      </div>
    </div>
  );
}

/** How the account is invoiced, its QuickBooks customer and terms, who set it, what moves at renewal, and its history. */
export function ChannelCard(p: {
  channel: InvoiceChannelView;
  account: PartnerAccount;
  canWrite: boolean;
  onChange: () => void;
}): ReactNode {
  const c = p.channel;
  const facts = [
    { label: 'QuickBooks customer', value: c.quickbooksCustomer ?? '—' },
    { label: 'Terms', value: `Net ${c.termsDays}` },
    { label: 'Set', value: c.setAt ? day(c.setAt) : 'Never switched', note: c.setBy },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice channel</CardTitle>
      </CardHeader>
      <ChannelHero c={c} />
      <FactList facts={facts} spaced />
      {c.moving.length > 0 ? (
        <Banner
          title={`${companiesWord(c.moving.length)} ${c.moving.length === 1 ? 'moves at its' : 'move at their'} renewal`}
        >
          {c.moving.map((m) => `${m.name ?? m.tenantId} on ${day(m.movesOn)}`).join('; ')}.
        </Banner>
      ) : null}
      {p.canWrite ? (
        <div className="pb-actions">
          <Button onClick={p.onChange}>Change channel…</Button>
        </div>
      ) : null}
      <History account={p.account} />
    </Card>
  );
}

function History({ account }: { account: PartnerAccount }): ReactNode {
  if (account.changes.length === 0) return null;
  return (
    <>
      <h4 className="pb-section-title">History</h4>
      <ul className="pb-history">
        {account.changes.map((h) => (
          <li key={`${h.at}-${h.to}`}>
            {channelWord[h.from]} to {channelWord[h.to]}
            {h.companiesMoving > 0
              ? `, ${companiesWord(h.companiesMoving)} at their renewal`
              : ''}: {h.reason}
            <div className="pb-history__who">
              {day(h.at)} · {h.actor} · {h.via}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function StandingBanner({ s }: { s: PartnerAccount['suspension'] }): ReactNode {
  if (s.state === 'suspended')
    return (
      <Banner
        tone="warning"
        title={`Suspended ${day(s.suspendedAt)} by ${s.suspendedBy ?? 'staff'}`}
      >
        {s.why ?? ''} Every company it pays for is on Free until a person resumes it.
      </Banner>
    );
  if (s.state === 'lapsed')
    return (
      <Banner tone="danger" title="Lapsed for an unpaid online invoice">
        It ends when that invoice is paid; it is not resumed by hand.
      </Banner>
    );
  return null;
}

/** The account's standing: good, lapsed for an online invoice, or suspended by hand; with Suspend or Resume. */
export function StandingCard(p: {
  account: PartnerAccount;
  canWrite: boolean;
  onSuspend: () => void;
  onResume: () => void;
}): ReactNode {
  const s = p.account.suspension;
  const rate =
    p.account.kind === 'distributor'
      ? 'on its dealers’ companies; 40% on its own'
      : 'on every company';
  const facts = [
    { label: 'Pays for', value: companiesWord(p.account.companies) },
    { label: 'Rate', value: `${p.account.discountPercent}% off list`, note: rate },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Standing</CardTitle>
        <Badge tone={stateTone[s.state]}>{stateWord[s.state]}</Badge>
      </CardHeader>
      <FactList facts={facts} />
      <StandingBanner s={s} />
      {p.canWrite && s.state !== 'lapsed' ? (
        <div className="pb-actions">
          {s.state === 'suspended' ? (
            <Button onClick={p.onResume}>Resume account…</Button>
          ) : (
            <Button variant="danger" onClick={p.onSuspend}>
              Suspend account…
            </Button>
          )}
        </div>
      ) : null}
    </Card>
  );
}
